import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { db } from "../firebase/firestore";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useUser } from "../context/userContext";
import { useAuthContext } from "../context/AuthContext";
import { usePremiumCheck } from './hooks/usePremiumCheck';
import SubscriptionPopup from "../Components/SubscriptionPopup";
import { 
  Play, Music, BookOpen, ExternalLink, FileText, 
  Video, Lock, Crown, GraduationCap
} from 'lucide-react';

const CourseBlockDetail = () => {
  const { id, blockId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [userCourses, setUserCourses] = useState([]);
  const [userCoursesAccess, setUserCoursesAccess] = useState([]);
  const [giftTimers, setGiftTimers] = useState({});
  const navigationInProgressRef = useRef(false);
  
  const [lessonImages, setLessonImages] = useState({});
  const [trainingImages, setTrainingImages] = useState({});
  
  const { 
    premium: userIsPremium, 
    id: userId, 
    loading: userLoading,
    subscriptionData,
    userType
  } = useUser();
  
  const { user: firebaseUser } = useAuthContext();
  
  const { 
    loading: premiumLoading
  } = usePremiumCheck(userId);

  const effectivePremium = userIsPremium;

  // Получение имени коллекции в зависимости от типа пользователя
  const getCollectionName = useCallback(() => {
    if (userType === 'web') {
      return 'webUsers';
    }
    return 'telegramUsers';
  }, [userType]);

  // Функция для загрузки курсов пользователя из БД
  const fetchUserCourses = useCallback(async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const userDocRef = doc(db, collectionName, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const courses = userData.courses || [];
        const coursesAccess = userData.coursesAccess || [];
        setUserCourses(courses);
        setUserCoursesAccess(coursesAccess);
        return courses;
      }
      return [];
    } catch (error) {
      console.error("Error fetching user courses:", error);
      return [];
    }
  }, [userId, getCollectionName]);

  // Проверка, не истек ли доступ к курсу
  const isCourseAccessExpired = useCallback((courseId) => {
    const access = userCoursesAccess.find(a => a.id === courseId);
    if (!access || !access.endDate) return false;
    
    const now = new Date();
    const endDate = new Date(access.endDate);
    return endDate < now;
  }, [userCoursesAccess]);

  // Функция для загрузки подарков пользователя
  const fetchUserGifts = useCallback(async () => {
    if (!userId) return [];

    try {
      const collectionName = getCollectionName();
      const giftsRef = collection(db, collectionName, userId, "gifts");
      const q = query(giftsRef, where("status", "==", "active"));
      const querySnapshot = await getDocs(q);
      
      const gifts = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
        const now = new Date();
        
        if (expiresAt > now) {
          gifts.push({
            id: doc.id,
            ...data,
            expiresAt: expiresAt
          });
        }
      });
      
      const newTimers = {};
      gifts.forEach(gift => {
        const expiresAt = new Date(gift.expiresAt);
        const now = new Date();
        
        if (expiresAt > now) {
          newTimers[gift.itemId] = {
            isActive: true,
            expiresAt: expiresAt,
            duration: gift.duration,
            timeLeft: Math.floor((expiresAt - now) / 1000),
            giftData: gift
          };
        }
      });
      
      setGiftTimers(newTimers);
      return gifts;
    } catch (error) {
      console.error("Error fetching user gifts:", error);
      return [];
    }
  }, [userId, getCollectionName]);

  // Загружаем данные пользователя при монтировании
  useEffect(() => {
    if (userId) {
      Promise.all([
        fetchUserCourses(),
        fetchUserGifts()
      ]);
    }
  }, [userId, fetchUserCourses, fetchUserGifts]);

  // Обновляем таймеры подарков каждую секунду
  useEffect(() => {
    if (!userId || Object.keys(giftTimers).length === 0) return;

    const interval = setInterval(() => {
      setGiftTimers(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(key => {
          if (updated[key].timeLeft > 0) {
            updated[key].timeLeft -= 1;
            hasChanges = true;
          } else {
            delete updated[key];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [userId, giftTimers]);

  // Функция для обработки нажатия кнопки назад
  const handleBackButton = useCallback(() => {
    if (navigationInProgressRef.current) {
      console.log('Navigation already in progress, skipping');
      return;
    }

    console.log('CourseBlockDetail: Back button clicked, navigating back');
    navigationInProgressRef.current = true;
    
    try {
      navigate(-1);
    } catch (error) {
      console.error('Navigation error:', error);
      navigate('/', { replace: true });
    } finally {
      setTimeout(() => {
        navigationInProgressRef.current = false;
      }, 300);
    }
  }, [navigate]);

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      
      tg.ready();
      tg.expand();
      
      tg.BackButton.backgroundColor = '#000000';
      tg.BackButton.textColor = '#ffffff';
      
      tg.BackButton.offClick();
      tg.BackButton.onClick(handleBackButton);
      tg.BackButton.isVisible = true;
      
      console.log('CourseBlockDetail: Telegram back button initialized');
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.BackButton.offClick(handleBackButton);
        tg.BackButton.isVisible = false;
      }
    };
  }, [handleBackButton, userType]);

  // Сбрасываем флаг навигации при изменении маршрута
  useEffect(() => {
    navigationInProgressRef.current = false;
  }, [location.pathname]);

  // Загрузка данных курса и блока
  useEffect(() => {
    const loadData = async () => {
      try {
        if (location.state?.block && location.state?.course) {
          console.log("Using data from location.state");
          setCourse(location.state.course);
          setBlock(location.state.block);
          
          if (location.state.block.contents) {
            await loadContentImages(location.state.block.contents);
          }
          
          setLoading(false);
          return;
        }

        console.log("Loading from Firebase");
        const courseRef = doc(db, "courses", id);
        const courseSnap = await getDoc(courseRef);
        
        if (courseSnap.exists()) {
          const courseData = {
            id: courseSnap.id,
            ...courseSnap.data()
          };
          setCourse(courseData);
          
          if (courseData.blocks) {
            const foundBlock = courseData.blocks.find(b => b.id === blockId);
            if (foundBlock) {
              console.log("Block found in Firebase:", foundBlock);
              setBlock(foundBlock);
              
              if (foundBlock.contents) {
                await loadContentImages(foundBlock.contents);
              }
            } else {
              console.error("Block not found in Firebase");
            }
          }
        } else {
          console.error("Course not found");
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    const loadContentImages = async (contents) => {
      const lessonPromises = [];
      const trainingPromises = [];
      
      contents.forEach(content => {
        if (content.type === 'lesson' && content.data?.id && !lessonImages[content.data.id]) {
          lessonPromises.push(
            getDoc(doc(db, "masterClasses", content.data.id))
              .then(snap => {
                if (snap.exists()) {
                  const imageUrl = snap.data().image || '';
                  setLessonImages(prev => ({
                    ...prev,
                    [content.data.id]: imageUrl
                  }));
                }
              })
              .catch(err => console.error("Error loading lesson image:", err))
          );
        }
        
        if (content.type === 'training' && content.data?.id && !trainingImages[content.data.id]) {
          trainingPromises.push(
            getDoc(doc(db, "trainings", content.data.id))
              .then(snap => {
                if (snap.exists()) {
                  const imageUrl = snap.data().image || '';
                  setTrainingImages(prev => ({
                    ...prev,
                    [content.data.id]: imageUrl
                  }));
                }
              })
              .catch(err => console.error("Error loading training image:", err))
          );
        }
      });
      
      await Promise.all([...lessonPromises, ...trainingPromises]);
    };

    if (id && blockId) {
      loadData();
    }
  }, [id, blockId, location.state]);

  // Функция для проверки доступа к курсу
  const checkCourseAccess = useCallback(() => {
    if (!course) return { hasAccess: false, reason: 'no_course' };
    
    console.log(`🔍 Проверка доступа к курсу ${course.title || course.id} в блоке:`, {
      courseId: course.id,
      userId,
      userType,
      isPremium: effectivePremium,
      userCourses,
      giftTimers: giftTimers[course.id]
    });

    // 1. Проверка подарка
    if (userId && giftTimers[course.id]) {
      return {
        hasAccess: true,
        reason: 'gift',
        message: 'Подарок',
        giftData: giftTimers[course.id]
      };
    }

    // 2. Проверка индивидуального доступа через courses с проверкой срока
    if (userId && userCourses.includes(course.id)) {
      const isExpired = isCourseAccessExpired(course.id);
      if (!isExpired) {
        console.log(`✅ Доступ разрешен: курс ${course.id} найден в массиве courses пользователя`);
        return {
          hasAccess: true,
          reason: 'user_course',
          message: 'Индивидуальный доступ'
        };
      } else {
        console.log(`❌ Доступ к курсу истек: ${course.id}`);
      }
    }
    
    // 3. Проверка бесплатного курса
    if (!course.premium && !course.paidAccessId) {
      return { 
        hasAccess: true, 
        reason: 'free',
        message: 'Доступен'
      };
    }

    // 4. Проверка авторизации
    if (!userId) {
      return { 
        hasAccess: false, 
        reason: 'no_auth', 
        requiresAuth: true,
        message: 'Требуется авторизация'
      };
    }

    // 5. Проверка Premium подписки
    if (course.premium && effectivePremium) {
      return { 
        hasAccess: true, 
        reason: 'premium',
        message: 'Premium доступ'
      };
    }

    // 6. Проверка paidAccessId
    if (course.paidAccessId && course.paidAccessId === userId) {
      console.log(`✅ Доступ разрешен: paidAccessId ${course.paidAccessId} совпадает с userId`);
      return { 
        hasAccess: true, 
        reason: 'paid_access',
        message: 'Персональный доступ'
      };
    }

    // 7. Для веб-пользователей проверка по email
    if (userType === 'web' && firebaseUser?.email && course.paidAccessEmail) {
      if (course.paidAccessEmail === firebaseUser.email) {
        console.log(`✅ Доступ разрешен: paidAccessEmail ${course.paidAccessEmail} совпадает с email пользователя`);
        return {
          hasAccess: true,
          reason: 'paid_access_email',
          message: 'Персональный доступ'
        };
      }
    }

    // 8. Требуется Premium
    if (course.premium) {
      return { 
        hasAccess: false, 
        reason: 'premium_required', 
        requiresPremium: true,
        message: 'Premium'
      };
    }

    return { 
      hasAccess: false, 
      reason: 'unknown',
      message: 'Premium'
    };
  }, [course, userId, userType, effectivePremium, userCourses, giftTimers, isCourseAccessExpired, firebaseUser]);

  const access = checkCourseAccess();
  const showPremiumBadge = course?.premium || course?.paidAccessId;

  let accessBadge = null;
  if (access.hasAccess) {
    if (access.reason === 'gift') {
      accessBadge = { text: '🎁 Подарок', color: 'from-pink-500 to-purple-500' };
    } else if (access.reason === 'user_course') {
      accessBadge = { text: '👤 Индивидуальный', color: 'from-blue-500 to-cyan-500' };
    } else if (access.reason === 'premium') {
      accessBadge = { text: '⭐ PRO', color: 'from-yellow-500 to-orange-500' };
    } else if (access.reason === 'paid_access' || access.reason === 'paid_access_email') {
      accessBadge = { text: '🔑 Персональный', color: 'from-green-500 to-emerald-500' };
    } else if (access.reason === 'free') {
      accessBadge = { text: '🆓 Бесплатный', color: 'from-gray-500 to-gray-600' };
    }
  }

  // Функция для получения изображения по типу контента
  const getContentImage = (content) => {
    if (content.type === 'lesson' && content.data?.id) {
      return lessonImages[content.data.id] || '';
    }
    
    if (content.type === 'training' && content.data?.id) {
      return trainingImages[content.data.id] || '';
    }
    
    if ((content.type === 'audio' || content.type === 'course_audio') && content.data?.image) {
      return content.data.image;
    }
    
    return '';
  };

  // Функция для получения названия типа контента
  const getContentTypeName = (type) => {
    switch(type) {
      case 'video': return 'Видео';
      case 'audio': return 'Аудиоурок';
      case 'course_audio': return 'Аудио курса';
      case 'lesson': return 'Урок';
      case 'training': return 'Тренинг';
      case 'button': return 'Ссылка';
      case 'text': return 'Текст';
      default: return type;
    }
  };

  // Функция для получения иконки по типу контента
  const getContentIcon = (type) => {
    switch(type) {
      case 'video': return <Video className="w-8 h-8 text-purple-400" />;
      case 'audio': return <Music className="w-8 h-8 text-green-400" />;
      case 'course_audio': return <Music className="w-8 h-8 text-green-400" />;
      case 'lesson': return <BookOpen className="w-8 h-8 text-blue-400" />;
      case 'training': return <GraduationCap className="w-8 h-8 text-orange-400" />;
      case 'button': return <ExternalLink className="w-8 h-8 text-red-400" />;
      case 'text': return <FileText className="w-8 h-8 text-gray-400" />;
      default: return <FileText className="w-8 h-8 text-gray-400" />;
    }
  };

  // Обработчик клика по контенту
  const handleContentClick = (content) => {
    console.log("🔍 Клик по контенту:", content.type, content.data);
    
    if (!access.hasAccess) {
      if (access.requiresPremium) {
        setShowSubscriptionPopup(true);
      }
      return;
    }

    switch(content.type) {
      case 'video':
        if (content.data.kinescopeId) {
          console.log("Навигация на видео:", content.data.kinescopeId);
          navigate(`/video/${content.data.kinescopeId}`);
        }
        break;
        
      case 'audio':
        if (content.data.id) {
          console.log("Навигация на аудиоурок:", content.data.id);
          navigate(`/AudioLessonsPage/${content.data.id}`);
        } else if (content.data.audioUrl) {
          console.log("Открытие аудио:", content.data.audioUrl);
          navigate(`/course-audio/${encodeURIComponent(content.data.audioUrl)}`, {
            state: {
              title: content.data.title || 'Аудио',
              audioUrl: content.data.audioUrl,
              duration: content.data.duration,
              fileName: content.data.fileName
            }
          });
        }
        break;
        
      case 'course_audio':
        if (content.data.audioUrl) {
          navigate(`/course-audio/${encodeURIComponent(content.data.audioUrl)}`, {
            state: {
              title: content.data.title || 'Аудио курса',
              audioUrl: content.data.audioUrl,
              duration: content.data.duration,
              fileName: content.data.fileName
            }
          });
        }
        break;
        
      case 'lesson':
        if (content.data.id) {
          console.log("Навигация на урок:", content.data.id);
          navigate(`/masterClasses/${content.data.id}`);
        }
        break;
        
      case 'training':
        if (content.data.id) {
          console.log("Навигация на тренинг:", content.data.id);
          navigate(`/training/${content.data.id}`);
        }
        break;
        
      case 'button':
        if (content.data.url) {
          console.log("Открытие ссылки:", content.data.url);
          window.open(content.data.url, content.data.target || '_blank');
        }
        break;
        
      case 'text':
        if (content.data.text) {
          alert(content.data.text);
        }
        break;
        
      default:
        console.log("Неизвестный тип контента:", content.type);
        break;
    }
  };

  // Форматирование времени для таймера
  const formatTimeLeft = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00";
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}д ${hours}ч`;
    } else if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  if (loading || userLoading || premiumLoading) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-white text-lg">Загрузка...</div>
        </div>
      </div>
    );
  }

  if (!course || !block) {
    return (
      <div 
        className="fixed inset-0 flex items-center justify-center"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="text-white text-xl">Блок не найден</div>
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
        }}
      />

      <div className="w-full min-h-screen px-4 pt-6 pb-24 relative z-10">
        {/* Заголовок блока */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-white text-[24px] font-semibold">
              {block.title}
            </h1>
            {showPremiumBadge && !accessBadge && (
              <span className="inline-block px-2 py-1 bg-gradient-to-r from-[#FF4757] to-[#FF6B6B] rounded-lg text-white text-xs font-medium shadow-lg">
                Premium
              </span>
            )}
            {accessBadge && (
              <span className={`inline-block px-2 py-1 rounded-lg text-white text-xs font-medium shadow-lg bg-gradient-to-r ${accessBadge.color}`}>
                {accessBadge.text}
              </span>
            )}
          </div>

          {block.description && (
            <p className="text-gray-400 text-sm mt-2">{block.description}</p>
          )}

          {access.hasAccess && access.reason === 'gift' && access.giftData && (
            <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg">
              <span className="mr-1">🎁</span>
              Подарок активен: {formatTimeLeft(access.giftData.timeLeft)}
            </div>
          )}
        </div>

        {/* Статус доступа */}
        {!access.hasAccess && access.requiresPremium && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/40 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium flex items-center">
                  <Lock className="w-4 h-4 mr-2" />
                  Требуется Premium доступ
                </p>
                <p className="text-sm text-white/70 mt-1">
                  Этот материал доступен только по Premium подписке
                </p>
              </div>
              <button 
                className="px-6 py-2 rounded-full text-white text-[15px] font-medium active:scale-95 transition bg-gradient-to-r from-[#6C6FF8] to-[#F466C8] hover:opacity-90"
                onClick={() => setShowSubscriptionPopup(true)}
              >
                Получить PRO
              </button>
            </div>
          </div>
        )}

        {/* Список контента */}
        <div className="flex flex-col gap-4">
          {block.contents && block.contents.length > 0 ? (
            block.contents.map((content, index) => {
              const imageUrl = getContentImage(content);
              const hasImage = imageUrl && imageUrl.trim() !== '';
              const isLocked = !access.hasAccess;
              
              return (
                <div
                  key={content.id || index}
                  className={`w-full rounded-[20px] border p-4 flex items-center gap-4 ${isLocked ? 'opacity-70' : 'cursor-pointer hover:opacity-90 transition-opacity'}`}
                  style={{
                    background: "#241352",
                    borderColor: isLocked ? "#EF4444" : "rgba(255,255,255,0.2)"
                  }}
                  onClick={() => handleContentClick(content)}
                >
                  {/* Картинка */}
                  <div className="w-[79px] h-[79px] rounded-[12px] overflow-hidden shrink-0 bg-black/30 flex items-center justify-center">
                    {hasImage ? (
                      <img
                        src={imageUrl}
                        alt={content.data?.title || getContentTypeName(content.type)}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-full flex items-center justify-center">' + 
                              (content.type === 'video' ? '<svg class="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>' : 
                               (content.type === 'audio' || content.type === 'course_audio') ? '<svg class="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>' :
                               content.type === 'lesson' ? '<svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>' :
                               content.type === 'training' ? '<svg class="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path></svg>' :
                               '<svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>') + 
                              '</div>';
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {getContentIcon(content.type)}
                      </div>
                    )}
                  </div>

                  {/* Контент */}
                  <div className="flex-1 flex flex-col justify-between min-h-[72px]">
                    <div>
                      <div className="flex items-center gap-2">
                        <div
                          className="text-[16px] leading-tight font-medium break-words whitespace-normal"
                          style={{ color: isLocked ? "#94A3B8" : "#C8AFE8" }}
                        >
                          {content.type === 'video' && (content.data?.title || 'Видеоурок')}
                          {content.type === 'audio' && (content.data?.title || 'Аудиоурок')}
                          {content.type === 'course_audio' && (content.data?.title || 'Аудио курса')}
                          {content.type === 'lesson' && (content.data?.title || 'Урок')}
                          {content.type === 'training' && (content.data?.title || 'Тренинг')}
                          {content.type === 'button' && (content.data?.text || 'Перейти по ссылке')}
                          {content.type === 'text' && 'Текстовый материал'}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className="text-[14px] font-medium"
                          style={{ color: isLocked ? "#EF4444" : "#34D3FF" }}
                        >
                          {isLocked ? "Заблокировано" : "Доступно"}
                        </div>
                        {(content.type === 'audio' || content.type === 'course_audio') && content.data?.duration && (
                          <div className="text-xs text-gray-400">
                            {content.data.duration}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Кнопка */}
                    <div className="mt-3">
                      <button
                        className={`px-4 py-1.5 rounded-full text-white text-[13px] font-medium active:scale-95 transition ${isLocked ? 'cursor-not-allowed' : 'hover:opacity-90'}`}
                        style={{
                          background: isLocked ? "#6B7280" : "#6B3FD8",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContentClick(content);
                        }}
                        disabled={isLocked}
                      >
                        {isLocked ? "Заблокировано" : "Смотреть"}
                      </button>
                      
                      {isLocked && access.requiresPremium && !effectivePremium && userId && (
                        <p className="text-xs text-yellow-400 mt-2 text-center">
                          Требуется Premium подписка
                        </p>
                      )}
                      
                      {isLocked && !userId && (
                        <p className="text-xs text-pink-400 mt-2 text-center">
                          Требуется авторизация
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 bg-white/5 rounded-2xl backdrop-blur-sm">
              <p className="text-white/60">В этом блоке пока нет материалов</p>
            </div>
          )}
        </div>
      </div>

      <SubscriptionPopup
        isOpen={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
        subscriptionInfo={{
          active: effectivePremium,
          userId: userId,
          stripeCustomerId: subscriptionData?.stripeCustomerId,
          price: subscriptionData?.price || 55,
          currency: subscriptionData?.currency || '€',
          period: 'в месяц',
          features: [
            'Полный доступ ко всем курсам',
            'Доступ к премиум материалам',
            'Персональная обратная связь',
            'Доступ к закрытому сообществу'
          ]
        }}
      />
    </>
  );
};

export default CourseBlockDetail;